<template>
  <b-modal
    ref="circuitModal"
    id="circuitModal"
    :title="title"
    size="lg"
    header-bg-variant="dark"
    header-text-variant="light"
    :hide-footer="true"
    body-bg-variant="light"
    @hide="resetModal"
    @shown="initializeModal"
    :no-fade="true"
    :static="true"
  >
    <b-modal id="delete-pole-modal-circuit" hide-footer title="Delete Pole">
      <p class="my-4">
        <b>Are You sure that You want to delete this pole?</b>
      </p>
      <b-button
        class="mr-1"
        size="sm"
        variant="danger"
        @click="$bvModal.hide('delete-pole-modal-circuit')"
      >
        No
      </b-button>
      <b-button
        class="mr-1"
        size="sm"
        variant="success"
        @click="deletePole(deletedPole.pole_uuid, deletedPole.pole_substation_name)"
      >
        Yes
      </b-button>
    </b-modal>

    <InstallationModal
      title="Install LineSIGHT"
      id="sentinel-install-modal"
      refName="deviceInstallModal"
      :poleToInstallDevice="poleToInstallDevice"
      @updateCircuit="updateCircuit"
    />

    <UninstallationModal
      class="pt-10"
      :device="deviceToUninstall"
      :modalId="'sentinel-uninstall-modal'"
      @modalReset="handleUninstallModalReset"
      @updateCircuit="updateCircuit"
    />

    <b-container class="bootstrap-container" fluid>
      <b-container fluid>
        <b-row>
          <b-col md="4">
            <div class="circuit-information-container">
              <div class="circuit-information">
                <b-form-group label="Circuit Primary">
                  <b-form-input v-model="circuitPrimary" type="text"></b-form-input>
                  <div v-if="isCircuitPrimaryInvalid" class="invalid-feedback d-block">
                    Circuit Primary must be at least 1 character long and less than 41
                  </div>
                </b-form-group>

                <b-form-group label="Circuit Reference">
                  <b-form-input v-model="circuitReference" type="text"></b-form-input>
                  <div v-if="isCircuitReferenceInvalid" class="invalid-feedback d-block">
                    Circuit Reference must be at least 1 character long and less than 16
                  </div>
                </b-form-group>

                <b-form-group label="Circuit Name">
                  <b-form-input v-model="circuitName" type="text"></b-form-input>
                  <div v-if="isCircuitNameInvalid" class="invalid-feedback d-block">
                    Circuit name must be at least 3 characters long
                  </div>
                </b-form-group>

                <b-form-group label="Circuit Voltage">
                  <b-form-radio-group
                    v-model="selected_circuit_voltage"
                    :options="circuit_voltage_options"
                    buttons
                    button-variant="outline-secondary"
                    class="w-100"
                  ></b-form-radio-group>
                </b-form-group>

                <b-form-group label="Active Circuit">
                  <b-form-radio-group
                    v-model="isCircuitActive"
                    :options="circuit_active_options"
                    buttons
                    button-variant="outline-secondary"
                    class="w-100"
                  ></b-form-radio-group>
                </b-form-group>

                <b-form-group
                  v-if="isMultiRegional"
                  label="Circuit Region"
                >
                  <b-form-select
                    v-model="selectedCircuitRegion"
                    :options="circuitRegionOptions"
                  />
                </b-form-group>

                <b-form-group label="Upload Circuit GeoJSON File">
                  <b-form-file
                    v-model="modalUploadedGeoJson"
                    @change="onUploadGeojson"
                    placeholder="Upload Circuit GeoJSON file"
                    drop-placeholder="Drop file here"
                    class="mt-2"
                  ></b-form-file>
                </b-form-group>
              </div>
            </div>
          </b-col>

          <b-col md="8">
            <l-map :zoom="map.zoom" :bounds="uploadedGeoJsonBounds" ref="polesMap">
              <l-tile-layer :url="map.url" :attribution="map.attribution"></l-tile-layer>
              <l-geo-json
                v-if="canDisplayGeoJson"
                :geojson="uploadedGeoJson"
                :options="geoJsonOptions(selected_circuit_voltage)"
                ref="geojsonRef"
              />
            </l-map>
          </b-col>
        </b-row>
      </b-container>

      <b-container fluid>
        <b-row>
          <b-col>
            <b-row class="align-items-center mb-2">
              <b-col class="d-flex justify-content-end">
                <b-button
                  v-if="circuitModalMode === 'Edit'"
                  variant="success"
                  :disabled="disableAddNewPole"
                  class="add-pole-button"
                  @click.prevent="addPoleRow"
                >
                  <i class="fas fa-plus"></i> Add New Pole
                </b-button>
              </b-col>
            </b-row>

            <ResponsiveTable
              v-if="poles.length && circuitModalMode === 'Edit'"
              :items="poles"
              :fields="polesFields"
              :rowsPerPage="10"
              :search-enabled="false"
              sort-by="created"
              sort-direction="desc"
            >
              <template #cell(pole_substation_name)="row">
                <div
                  v-if="!(addNewPole && row.item.uuid === 'temp-id')"
                  class="text-wrap-cell"
                >
                  {{ row.item.pole_substation_name }}
                </div>
                <b-form-input
                  v-else
                  :class="{ 'is-invalid': isSubstationNameInvalid }"
                  v-model="newPole.name"
                  type="text"
                  placeholder="Substation Name"
                />
              </template>

              <template #cell(pole_substation_number)="row">
                <div
                  v-if="!(addNewPole && row.item.uuid === 'temp-id')"
                  class="text-wrap-cell"
                >
                  {{ row.item.pole_substation_number }}
                </div>
                <b-form-input
                  v-else
                  :class="{ 'is-invalid': isSubstationNumberInvalid }"
                  v-model="newPole.number"
                  type="text"
                  placeholder="Substation Number"
                />
              </template>

              <template #cell(hv_pole_number)="row">
                <div
                  v-if="!(addNewPole && row.item.uuid === 'temp-id')"
                  class="text-wrap-cell"
                >
                  {{ row.item.hv_pole_number }}
                </div>
                <b-form-input
                  v-else
                  :class="{ 'is-invalid': isHVPoleNumberInvalid }"
                  v-model="newPole.pole_number"
                  type="text"
                  placeholder="HV Pole Number"
                />
              </template>

              <template #cell(coordinates)="row">
                <div
                  v-if="!(addNewPole && row.item.uuid === 'temp-id')"
                  class="text-wrap-cell"
                >
                  {{ row.item.coordinates.latitude }}, {{ row.item.coordinates.longitude }}
                </div>
                <div v-else class="coordinates-container">
                  <b-form-input
                    :class="{ 'is-invalid': isLatitudeInvalid }"
                    v-model="latitude"
                    placeholder="Latitude"
                    class="table-input"
                  />
                  <b-form-input
                    :class="{ 'is-invalid': isLongitudeInvalid }"
                    v-model="longitude"
                    placeholder="Longitude"
                  />
                </div>
              </template>

              <template #cell(type)="row">
                <div
                  v-if="!(addNewPole && row.item.uuid === 'temp-id')"
                  class="text-wrap-cell"
                >
                  {{ row.item.type }}
                </div>
                <b-form-select
                  v-else
                  id="new-pole-type"
                  :required="false"
                  :options="pole_types"
                  v-model="newPole.type"
                />
              </template>

              <template #cell(notes)="row">
                <div
                  v-if="!(addNewPole && row.item.uuid === 'temp-id')"
                  class="text-wrap-cell"
                >
                  {{ row.item.notes }}
                </div>
                <b-form-input
                  v-else
                  v-model="newPole.notes"
                  type="text"
                  placeholder="Notes"
                  class="table-input"
                />
              </template>

              <template #cell(installed_device)="row">
                {{ row.item.device ? row.item.device.product_serial_num : '' }}
              </template>

              <template #cell(actions)="row">
                <div v-if="addNewPole && row.item.uuid === 'temp-id'" class="d-flex">
                  <span id="confirm-add-pole-wrapper" class="d-inline-block pr-2" tabindex="0">
                    <b-button
                      id="confirm-add-pole"
                      variant="success"
                      v-b-tooltip.hover
                      title="Saving Pole Creation"
                      :disabled="isCreateNewPoleDisabled"
                      class="confirm-add-pole-button"
                      @click.prevent="addPole"
                    >
                      Save
                    </b-button>
                  </span>

                  <b-tooltip
                    v-if="isCreateNewPoleDisabled"
                    target="confirm-add-pole-wrapper"
                    container="body"
                    :title="'Enter all required fields'"
                  />

                  <b-button
                    variant="danger"
                    @click.prevent="cancelPole"
                    v-b-tooltip.hover
                    title="Canceling Pole Creation"
                  >
                    Cancel
                  </b-button>
                </div>

                <div class="d-flex" v-else>
                  <b-button
                    variant="danger"
                    class="mr-2"
                    v-b-modal="'delete-pole-modal-circuit'"
                    :disabled="addNewPole"
                    @click="openDeleteModal(row.item.uuid, row.item.pole_substation_name)"
                  >
                    Delete Pole
                  </b-button>

                  <div v-if="row.item.device">
                    <b-button
                      v-if="$base.isAllowed('uninstallDevice')"
                      variant="warning"
                      class="mr-2"
                      :disabled="addNewPole"
                      @click="openUninstallationModal(row.item)"
                    >
                      Uninstall
                    </b-button>
                  </div>

                  <div v-else>
                    <b-button
                      v-if="$base.isAllowed('installDevice')"
                      variant="success"
                      class="mr-2"
                      v-b-modal="'sentinel-install-modal'"
                      :disabled="addNewPole"
                      @click="openInstallationModal(row.item)"
                    >
                      Install
                    </b-button>
                  </div>
                </div>
              </template>
            </ResponsiveTable>
          </b-col>
        </b-row>
      </b-container>

      <b-row no-gutters class="mb-3">
        <b-col class="text-right" align-self="end" md="12">
          <div v-if="circuitModalMode === 'Edit'">
            <b-modal id="delete-circuit-modal" hide-footer title="Delete Circuit">
              <p class="my-4">
                <b>Are You sure that You want to delete this circuit?</b>
              </p>
              <b-button
                class="mr-1"
                size="sm"
                variant="danger"
                @click="$bvModal.hide('delete-circuit-modal')"
              >
                No
              </b-button>
              <b-button
                class="mr-1"
                size="sm"
                variant="success"
                @click="deleteCircuit"
              >
                Yes
              </b-button>
            </b-modal>
          </div>

          <b-button
            variant="dark"
            class="mr-1"
            @click.prevent="hideModal"
            :disabled="isDeleteDisabled"
            v-b-modal="'delete-circuit-modal'"
            v-if="circuitModalMode === 'Edit'"
          >
            Delete
          </b-button>

          <b-button
            variant="danger"
            class="mr-1"
            @click.prevent="hideModal"
            :disabled="isAddingPole"
          >
            Cancel
          </b-button>

          <b-button
            variant="success"
            class="save-and-close mr-1"
            @click.prevent="callAddOrEditCircuit(false)"
            :disabled="isSaveDisabled"
          >
            Save
          </b-button>

          <b-button
            v-if="circuitModalMode === 'Add'"
            variant="success"
            class="save-and-add-pole"
            @click.prevent="callAddOrEditCircuit(true)"
            :disabled="isSaveDisabled"
          >
            Save &amp; Add Pole
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </b-modal>
</template>

<script>
import {
  LTileLayer,
  LMap,
  LImageOverlay,
  LMarker,
  LRectangle,
  LGeoJson
} from 'vue2-leaflet';

import {
  mapActions,
  mapState,
  mapGetters
} from 'vuex';

import isNumberTextField from '@/core/isNumberTextFieldHelper';
import { isEmpty } from 'lodash';
import { circuitService } from '@/_services';
import ResponsiveTable from '@/components/ResponsiveTable/ResponsiveTable';
import InstallationModal from '@/components/InstallationModal/InstallationModal';
import UninstallationModal from '@/components/UninstallationModal/UninstallationModal';

export default {
  name: 'CircuitModal',

  components: {
    LMap,
    LTileLayer,
    LImageOverlay,
    LMarker,
    LRectangle,
    LGeoJson,
    ResponsiveTable,
    InstallationModal,
    UninstallationModal
  },

  props: {
    title: {
      type: String,
      required: true
    },
    circuitModalMode: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    circuit: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      polesFields: [
        {
          key: 'pole_substation_name',
          label: 'Sub Name',
          sortable: true
        },
        {
          key: 'pole_substation_number',
          label: 'Sub Number',
          sortable: true
        },
        {
          key: 'hv_pole_number',
          label: 'Pole Number',
          sortable: true
        },
        {
          key: 'coordinates',
          label: 'Coordinates',
          sortable: true
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true
        },
        {
          key: 'notes',
          label: 'Notes',
          sortable: true
        },
        {
          key: 'installed_device',
          label: 'Installed Device',
          sortable: true
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false
        }
      ],

      polesSearchableFields: [],
      fields: ['latitude', 'longitude'],

      circuitName: '',
      circuitPrimary: '',
      circuitReference: '',

      selectedCircuitRegion: null,

      map: {
        zoom: 6,
        url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
        attribution: '&copy; http://osm.org/copyrightOpenStreetMap</a> contributors'
      },

      defaultMapBounds: [
        [49.6188108303, -11.0992536835],
        [59.7156195292, 2.1722306915]
      ],

      repositionDistance: 0.1,
      poles_header: ['poles'],
      poles_data: [],
      rectangleStyle: {
        color: 'red',
        weight: 2
      },

      uploadedGeoJson: null,
      uploadedGeoJsonBounds: null,
      modalUploadedGeoJson: null,
      canDisplayGeoJson: false,

      selected_circuit_voltage: '11kV',

      circuit_voltage_options: [
        {
          text: '11kV',
          value: '11kV'
        },
        {
          text: '6.6kV',
          value: '6.6kV'
        }
      ],

      circuit_active_options: [
        {
          text: 'Yes',
          value: true
        },
        {
          text: 'No',
          value: false
        }
      ],

      isCircuitActive: null,

      poles: [],
      addNewPole: false,
      disableAddNewPole: false,

      newPole: {
        name: '',
        number: '',
        pole_number: '',
        coordinates: {
          latitude: '',
          longitude: ''
        },
        type: null,
        notes: ''
      },

      pole_types: [
        {
          text: 'Single Phase',
          value: 'Single Phase'
        },
        {
          text: 'Three Phase',
          value: 'Three Phase'
        },
        {
          text: 'Voltage & Current',
          value: 'Voltage & Current'
        },
        {
          text: 'Undefined',
          value: null
        }
      ],

      deletedPole: {
        pole_uuid: null,
        pole_substation_name: null
      },

      poleToInstallDevice: null,
      deviceToUninstall: null,
      originalPoleCount: 0,
      isCircuitDirty: false,
      isInitializingModal: false
    };
  },

  watch: {
    circuit: {
      handler(newValue) {
        if (newValue && Array.isArray(newValue.poles)) {
          this.poles = newValue.poles;
        } else {
          this.poles = [];
        }
      },
      deep: true,
      immediate: true
    },

    circuitName() {
      this.markCircuitDirty();
    },

    circuitPrimary() {
      this.markCircuitDirty();
    },

    circuitReference() {
      this.markCircuitDirty();
    },

    selectedCircuitRegion() {
      this.markCircuitDirty();
    },

    selected_circuit_voltage() {
      this.markCircuitDirty();
    },

    isCircuitActive() {
      this.markCircuitDirty();
    },

    uploadedGeoJson: {
      handler() {
        this.markCircuitDirty();
      },
      deep: true
    }
  },

  computed: {
    ...mapState('layout', {
      hideInactiveCircuits: state => state.hideInactiveCircuits
    }),
    ...mapGetters('circuit', {
      circuitRegionOptions: 'customerRegionOptions',
      isMultiRegional: 'isMultiRegional'
    }),
    computedMapBounds() {
      return this.defaultMapBounds;
    },

    isDeleteDisabled() {
      if (this.addNewPole) {
        return true;
      }

      const currentPoleCount = this.poles.length;

      if (this.originalPoleCount === 0 && currentPoleCount === 0) {
        return false;
      }

      if (this.circuitModalMode === 'Edit') {
        return currentPoleCount > 0;
      }

      return false;
    },

    isCircuitFormInvalid() {
      const isRegionInvalid =
        this.isMultiRegional &&
        (
          this.selectedCircuitRegion === null ||
          this.selectedCircuitRegion === undefined ||
          this.selectedCircuitRegion === ''
        );

      return (
        this.normalizeString(this.circuitName) === '' ||
        this.normalizeString(this.circuitPrimary) === '' ||
        this.normalizeString(this.circuitReference) === '' ||
        this.isCircuitNameInvalid ||
        this.isCircuitPrimaryInvalid ||
        this.isCircuitReferenceInvalid ||
        isRegionInvalid
      );
    },

    isSaveDisabled() {
      if (this.isAddingPole) {
        return true;
      }

      if (this.isCircuitFormInvalid) {
        return true;
      }

      if (this.circuitModalMode === 'Edit') {
        return !this.isCircuitDirty;
      }

      return false;
    },

    isCircuitNameInvalid() {
      const value = this.normalizeString(this.circuitName);

      return value !== '' && value.length < 3;
    },

    isCircuitPrimaryInvalid() {
      const value = this.normalizeString(this.circuitPrimary);

      return value !== '' && value.length >= 41;
    },

    isCircuitReferenceInvalid() {
      const value = this.normalizeString(this.circuitReference);

      return value !== '' && value.length >= 16;
    },

    isCreateNewPoleDisabled() {
      return (
        this.newPole.name === '' ||
        this.newPole.pole_number === '' ||
        this.newPole.number === '' ||
        this.isHVPoleNumberInvalid ||
        this.isSubstationNumberInvalid ||
        this.isPoleCoordinatesInvalid ||
        this.isSubstationNameInvalid ||
        this.isPoleNotesInvalid
      );
    },

    minLatitude() {
      return Math.min(this.defaultMapBounds[0][0], this.defaultMapBounds[1][0]);
    },

    maxLatitude() {
      return Math.max(this.defaultMapBounds[0][0], this.defaultMapBounds[1][0]);
    },

    minLongitude() {
      return Math.min(this.defaultMapBounds[0][1], this.defaultMapBounds[1][1]);
    },

    maxLongitude() {
      return Math.max(this.defaultMapBounds[0][1], this.defaultMapBounds[1][1]);
    },

    isHVPoleNumberInvalid() {
      const poleNumberLength = this.newPole.pole_number.trim().length;

      return !(poleNumberLength >= 3 && poleNumberLength <= 20);
    },

    isSubstationNameInvalid() {
      const substationNameLength = this.newPole.name.trim().length;

      return !(substationNameLength >= 3 && substationNameLength <= 255);
    },

    isPoleNotesInvalid() {
      const poleNotesLength = this.newPole.notes.trim().length;

      return !(poleNotesLength < 1024);
    },

    isSubstationNumberInvalid() {
      const substationNumberLength = this.newPole.number.trim().length;

      return !(substationNumberLength >= 3 && substationNumberLength <= 20);
    },

    isLatitudeInvalid() {
      return (
        !this.latitude ||
        this.latitude === '-' ||
        this.latitude === '+' ||
        parseFloat(this.latitude) > parseFloat(this.maxLatitude) ||
        parseFloat(this.latitude) < parseFloat(this.minLatitude)
      );
    },

    isLongitudeInvalid() {
      return (
        !this.longitude ||
        this.longitude === '-' ||
        this.longitude === '+' ||
        parseFloat(this.longitude) > parseFloat(this.maxLongitude) ||
        parseFloat(this.longitude) < parseFloat(this.minLongitude)
      );
    },

    isPoleCoordinatesInvalid() {
      return this.isLatitudeInvalid || this.isLongitudeInvalid;
    },

    deletedPoleProps() {
      return this.deletedPole.pole_uuid;
    },

    latitude: {
      get() {
        return this.newPole.coordinates.latitude;
      },
      set(value) {
        this.newPole.coordinates.latitude = value;
      }
    },

    longitude: {
      get() {
        return this.newPole.coordinates.longitude;
      },
      set(value) {
        this.newPole.coordinates.longitude = value;
      }
    },

    isAddingPole() {
      return this.addNewPole === true;
    }
  },

  methods: {
    ...mapActions('alert', ['success', 'error']),
    ...mapActions('circuit', ['loadCustomerRegions']),

    normalizeString(value) {
      return value === null || value === undefined ? '' : String(value).trim();
    },

    markCircuitDirty() {
      if (this.isInitializingModal) {
        return;
      }

      if (this.circuitModalMode === 'Edit') {
        this.isCircuitDirty = true;
      }
    },

    callAddOrEditCircuit(addNewPole) {
      if (this.circuitModalMode === 'Edit') {
        if (addNewPole) {
          this.addPole();
        }

        this.callEditCircuit();
      } else {
        this.callAddCircuit(addNewPole);
      }
    },

    openUninstallationModal(item) {
      if (!item.device) return;

      this.deviceToUninstall = {
        product_serial_num: item.device.product_serial_num,
        pole: {
          uuid: item.uuid,
          circuit_name: item.circuit_name,
          pole_substation_number: item.pole_substation_number
        }
      };

      this.$bvModal.show('sentinel-uninstall-modal');
    },

    updateCircuit() {
      this.$emit('updateCircuit', this.circuit.uuid, true);
    },

    openInstallationModal(pole) {
      this.poleToInstallDevice = pole;
    },

    handleUninstallModalReset() {
      this.deviceToUninstall = null;
      this.$emit('updateCircuit', this.circuit.uuid, true);
    },

    openDeleteModal(pole_uuid, pole_substation_name) {
      this.deletedPole.pole_uuid = pole_uuid;
      this.deletedPole.pole_substation_name = pole_substation_name;
    },

    addPoleRow() {
      this.addNewPole = true;

      this.poles.unshift({
        uuid: 'temp-id',
        pole_substation_name: '',
        pole_substation_number: '',
        hv_pole_number: '',
        coordinates: {
          latitude: '',
          longitude: ''
        },
        type: null,
        notes: '',
        device: null
      });

      this.disableAddNewPole = true;
    },

    cancelPole() {
      this.poles = this.poles.filter(pole => pole.uuid !== 'temp-id');
      this.addNewPole = false;
      this.disableAddNewPole = false;
    },

    addPole() {
      const currentPole = {
        pole_substation_name: this.newPole.name.trim(),
        pole_substation_number: this.newPole.number.trim(),
        hv_pole_number: this.newPole.pole_number.trim(),
        coordinates: {
          longitude: Number(this.newPole.coordinates.longitude),
          latitude: Number(this.newPole.coordinates.latitude)
        },
        type: this.newPole.type,
        notes: this.newPole.notes
      };

      const jsonCurrentPole = JSON.stringify(currentPole);

      circuitService.addPole(this.circuit.uuid, jsonCurrentPole)
        .then(() => {
          this.resetAddNewPole();
          this.disableAddNewPole = false;
          this.$emit('updateCircuit', this.circuit.uuid, true);
        })
        .catch(error => {
          this.handleError(error);
        });
    },

    deletePole(pole_uuid, pole_substation_name) {
      circuitService.deletePole(this.circuit.uuid, pole_uuid)
        .then(() => {
          this.success('Successfully deleted pole ' + pole_substation_name);
          this.$emit('updateCircuit', this.circuit.uuid, true);
        })
        .catch(error => {
          this.handleError(error);
        });

      this.$bvModal.hide('delete-pole-modal-circuit');
    },

    geoJsonOptions(circuit_type) {
      const options = {
        style: {
          color: 'red'
        }
      };

      if (circuit_type === '6.6kV') {
        options.style = {};
      }

      return options;
    },

    buildCircuitPayload() {
      const currentCircuit = {
        name: this.normalizeString(this.circuitName),
        bounds: [
          {
            longitude: Number(this.mapCornerLong),
            latitude: Number(this.mapCornerLat)
          },
          {
            longitude: Number(this.oppMapCornerLong),
            latitude: Number(this.oppMapCornerLat)
          }
        ],
        circuit_type: this.selected_circuit_voltage,
        is_active: this.isCircuitActive,
        circuit_primary: this.normalizeString(this.circuitPrimary),
        circuit_reference: this.normalizeString(this.circuitReference),
        region: this.isMultiRegional ? this.selectedCircuitRegion : null
      };

      if (!isEmpty(this.uploadedGeoJson)) {
        currentCircuit.circuit_geojson = this.uploadedGeoJson;
        currentCircuit.bounds = this.formatBounds(this.uploadedGeoJsonBounds);
      }

      return currentCircuit;
    },

    callEditCircuit() {
      const currentCircuit = this.buildCircuitPayload();
      const uuid = this.circuit.uuid;

      const payload = {
        circuit: currentCircuit,
        uuid
      };

      this.editCircuit(payload)
        .then(() => {
          this.success('Successfully edited circuit');

          this.isCircuitDirty = false;

          this.hideModal();
          this.resetModal();
          this.$emit('updateCircuit', uuid, true);
        })
        .catch(error => {
          this.handleError(error);
        });
    },

    async editCircuit(payload) {
      return await circuitService.editCircuit(payload.circuit, payload.uuid);
    },

    async addCircuit(circuit) {
      return await circuitService.addCircuit(circuit);
    },

    async callAddCircuit(addNewPole) {
      const currentCircuit = this.buildCircuitPayload();

      this.addCircuit(currentCircuit)
        .then(async response => {
          this.success('Successfully added new circuit');
          this.resetModal();
          this.$emit('updateCircuit', response.data.uuid, true);

          if (addNewPole) {
            this.$emit('switchToEditAndAddPole', response.data.uuid);
          } else {
            this.$refs.circuitModal.hide();
          }
        })
        .catch(error => {
          this.handleError(error);
        });
    },

    handleError(error) {
      const errResp = error.response;
      const errorCode = errResp && errResp.status;
      let errorMessage;

      if (errorCode >= 400 && errorCode < 500) {
        errorMessage = errResp && errResp.data && errResp.data.message;
      } else {
        errorMessage = 'Something has went wrong, please contact support.';
      }

      this.error(errorMessage);
    },

    hideModal() {
      this.$refs.circuitModal.hide();
    },

    showModal() {
      this.$refs.circuitModal.show();
    },

    async initializeModal() {
      this.isInitializingModal = true;
      this.isCircuitDirty = false;

      await this.loadCustomerRegions();

      this.originalPoleCount = this.circuit && Array.isArray(this.circuit.poles)
        ? this.circuit.poles.length
        : 0;

      this.resetMap();
      this.updateDefaultValuesOfCircuit();

      this.$nextTick(() => {
        this.isCircuitDirty = false;
        this.isInitializingModal = false;
      });
    },

    isNumber: isNumberTextField,

    resetMap() {
      this.$refs.polesMap.mapObject.invalidateSize();

      this.mapCornerLat = this.defaultMapBounds[0][0];
      this.mapCornerLong = this.defaultMapBounds[0][1];
      this.oppMapCornerLat = this.defaultMapBounds[1][0];
      this.oppMapCornerLong = this.defaultMapBounds[1][1];

      this.$refs.polesMap.fitBounds(this.computedMapBounds);
    },

    resetModal() {
      this.$emit('modalReset');

      this.uploadedGeoJson = null;
      this.uploadedGeoJsonBounds = null;
      this.canDisplayGeoJson = false;
      this.modalUploadedGeoJson = null;
      this.selectedCircuitRegion = null;

      this.isCircuitDirty = false;
      this.isInitializingModal = false;

      this.resetAddNewPole();
    },

    resetAddNewPole() {
      this.newPole = {
        name: '',
        number: '',
        pole_number: '',
        coordinates: {
          latitude: '',
          longitude: ''
        },
        type: null,
        notes: ''
      };

      this.cancelPole();
    },

    processAndDisplayGeoJson(geoJsonData) {
      if (
        geoJsonData &&
        geoJsonData.type === 'FeatureCollection' &&
        Array.isArray(geoJsonData.features)
      ) {
        geoJsonData = {
          type: 'GeometryCollection',
          geometries: geoJsonData.features
            .filter(feature => feature.geometry && feature.geometry.type === 'LineString')
            .map(feature => feature.geometry)
        };
      } else if (
        geoJsonData &&
        geoJsonData.type === 'GeometryCollection' &&
        Array.isArray(geoJsonData.geometries)
      ) {
        geoJsonData = {
          type: 'GeometryCollection',
          geometries: geoJsonData.geometries
            .filter(geometry => geometry && geometry.type === 'LineString')
        };
      }

      this.uploadedGeoJson = geoJsonData;
      this.uploadedGeoJsonBounds = this.calculateGeoJsonBounds(this.uploadedGeoJson);
      this.canDisplayGeoJson = true;
    },

    onUploadGeojson(e) {
      const file = e.target.files[0];

      if (!file) {
        this.error('No file selected.');
        return;
      }

      const reader = new FileReader();

      reader.onload = readerFile => {
        let geoJsonData;

        try {
          geoJsonData = JSON.parse(readerFile.target.result);
        } catch (error) {
          this.error('A valid Circuit GeoJSON cannot be read from the selected file.');
          return;
        }

        this.processAndDisplayGeoJson(geoJsonData);
      };

      reader.readAsText(file);
    },

    formatBounds(bounds) {
      return [
        {
          latitude: bounds[0][0],
          longitude: bounds[0][1]
        },
        {
          latitude: bounds[1][0],
          longitude: bounds[1][1]
        }
      ];
    },

    calculateGeoJsonBounds(geoJson) {
      let min_ltd = null;
      let max_ltd = null;
      let min_lng = null;
      let max_lng = null;
      let counter = 0;

      const geometries = Array.isArray(geoJson && geoJson.geometries)
        ? geoJson.geometries.filter(geometry => geometry && geometry.type === 'LineString')
        : [];

      for (const geometry of geometries) {
        for (const line of geometry.coordinates || []) {
          const [lng, ltd] = line;

          if (counter === 0) {
            min_ltd = ltd;
            max_ltd = ltd;
            min_lng = lng;
            max_lng = lng;
            counter = 1;
            continue;
          }

          if (ltd > max_ltd) max_ltd = ltd;
          if (ltd < min_ltd) min_ltd = ltd;
          if (lng > max_lng) max_lng = lng;
          if (lng < min_lng) min_lng = lng;
        }
      }

      if (counter === 0) return null;

      return [
        [min_ltd, max_lng],
        [max_ltd, min_lng]
      ];
    },

    updateDefaultValuesOfCircuit() {
      if (this.circuitModalMode === 'Edit') {
        this.selected_circuit_voltage = this.circuit.circuit_type;
        this.circuitName = this.circuit.name;

        if (this.circuit.circuit_geojson) {
          this.processAndDisplayGeoJson(this.circuit.circuit_geojson);
        } else {
          this.uploadedGeoJson = null;
          this.uploadedGeoJsonBounds = null;
          this.canDisplayGeoJson = false;
        }

        this.uploadedGeoJsonBounds = this.circuit.bounds;
        this.canDisplayGeoJson = !isEmpty(this.uploadedGeoJson);
        this.isCircuitActive = this.circuit.is_active;

        if (this.canDisplayGeoJson) {
          this.$refs.polesMap.mapObject.invalidateSize();
          this.$refs.polesMap.fitBounds(this.uploadedGeoJsonBounds);
        }

        this.circuitReference = this.circuit.circuit_reference;
        this.circuitPrimary = this.circuit.circuit_primary;

        this.selectedCircuitRegion =
          this.isMultiRegional && this.circuitRegionOptions.length
            ? this.circuit.region || null
            : null;
      } else {
        this.circuitName = '';
        this.selected_circuit_voltage = '11kV';
        this.uploadedGeoJson = null;
        this.uploadedGeoJsonBounds = null;
        this.canDisplayGeoJson = false;
        this.isCircuitActive = true;
        this.circuitReference = '';
        this.circuitPrimary = '';

        this.selectedCircuitRegion =
          this.isMultiRegional && this.circuitRegionOptions.length
            ? this.circuitRegionOptions[0].value
            : null;
      }

      this.modalUploadedGeoJson = null;
    },

    deleteCircuit() {
      circuitService.deleteCircuit(this.circuit.uuid)
        .then(() => {
          this.success('Successfully deleted circuit ' + this.circuit.name);
          this.$emit('updateCircuit', this.circuit.uuid);
        })
        
        .catch(error => {
          this.handleError(error);
        });

      this.$bvModal.hide('delete-circuit-modal');
    }
  }
};
</script>
